import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ZOOM_PRESETS, isSameZoom, type FitMode } from './renderers/zoom'

/** What a page of the previewed file is to the reader. */
export type PreviewKind = 'document' | 'slides' | 'sheet'

interface PdfToolbarProps {
  kind: PreviewKind
  pageNumber: number
  numPages: number
  /** Hidden when there is nothing to page through, e.g. a one-page sheet. */
  showPageNavigation?: boolean
  onGoToPage: (page: number) => void
  /** Share of the real size, 1 = 100 %. */
  zoom: number
  fitMode: FitMode
  /** What each fit mode comes to right now; null until the first page is measured. */
  fitZooms: { width: number | null; page: number | null }
  canZoomIn: boolean
  canZoomOut: boolean
  onZoomIn: () => void
  onZoomOut: () => void
  onFit: (mode: 'width' | 'page') => void
  onZoomTo: (zoom: number) => void
  /** Left of everything else, e.g. a workbook's sheet tabs. */
  children?: ReactNode
}

const WHOLE_PAGE_LABEL: Record<PreviewKind, string> = {
  document: 'pdfViewer.fitWholePage',
  slides: 'pdfViewer.fitWholeSlide',
  sheet: 'pdfViewer.fitWholeSheet',
}

/**
 * The preview's one bar, below the document (cdb_webos#1477): sheet tabs or
 * page navigation on the left, the zoom on the right. The fit modes and the
 * fixed steps sit in the menu behind the percentage.
 */
export function PdfToolbar({
  kind,
  pageNumber,
  numPages,
  showPageNavigation = true,
  onGoToPage,
  zoom,
  fitMode,
  fitZooms,
  canZoomIn,
  canZoomOut,
  onZoomIn,
  onZoomOut,
  onFit,
  onZoomTo,
  children,
}: PdfToolbarProps) {
  const { t } = useTranslation()
  const percent = (value: number) => t('pdfViewer.percent', { value: Math.round(value * 100) })
  const slides = kind === 'slides'

  return (
    <div className="flex-shrink-0 flex items-center gap-2 rounded-lg border border-[var(--window-border-color)] bg-[var(--window-background)] px-1 py-1">
      <div className="flex min-w-0 flex-1 items-center">{children}</div>

      {showPageNavigation && numPages > 0 && (
        <div className="flex shrink-0 items-center gap-1 text-xs text-[var(--text-secondary)]">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onGoToPage(pageNumber - 1)}
            disabled={pageNumber <= 1}
            title={t(slides ? 'pdfViewer.previousSlide' : 'pdfViewer.previousPage')}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span>{t(slides ? 'pdfViewer.slide' : 'pdfViewer.page')}</span>
          <PageInput
            pageNumber={pageNumber}
            numPages={numPages}
            onGoToPage={onGoToPage}
            label={t(slides ? 'pdfViewer.slideCounter' : 'pdfViewer.pageCounter', {
              current: pageNumber,
              total: numPages,
            })}
          />
          <span>{t('pdfViewer.ofTotal', { total: numPages })}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onGoToPage(pageNumber + 1)}
            disabled={pageNumber >= numPages}
            title={t(slides ? 'pdfViewer.nextSlide' : 'pdfViewer.nextPage')}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      <div className="flex shrink-0 items-center rounded-md border border-[var(--window-border-color)]">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 rounded-r-none"
          onClick={onZoomOut}
          disabled={!canZoomOut}
          title={t('pdfViewer.zoomOut')}
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('pdfViewer.zoomMenu', { value: percent(zoom) })}
              className="flex h-7 min-w-[4.5rem] items-center justify-center gap-1 border-x border-[var(--window-border-color)] px-2 text-xs tabular-nums text-[var(--text-color)] hover:bg-[var(--sidebar-hover)]"
            >
              {percent(zoom)}
              <ChevronDown className="h-3 w-3 text-[var(--text-secondary)]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end" className="w-64">
            <ZoomItem checked={fitMode === 'width'} value={fitZooms.width} percent={percent} onSelect={() => onFit('width')}>
              {t('pdfViewer.fitToWidth')}
            </ZoomItem>
            <ZoomItem checked={fitMode === 'page'} value={fitZooms.page} percent={percent} onSelect={() => onFit('page')}>
              {t(WHOLE_PAGE_LABEL[kind])}
            </ZoomItem>
            <DropdownMenuSeparator />
            {ZOOM_PRESETS.map((preset) => (
              <ZoomItem
                key={preset}
                checked={fitMode === 'custom' && isSameZoom(zoom, preset)}
                onSelect={() => onZoomTo(preset)}
                note={preset === 1 ? t('pdfViewer.actualSize') : undefined}
              >
                {percent(preset)}
              </ZoomItem>
            ))}
            <DropdownMenuSeparator />
            <p className="px-3 py-1.5 text-xs text-[var(--text-secondary)]">{t('pdfViewer.zoomHint')}</p>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 rounded-l-none"
          onClick={onZoomIn}
          disabled={!canZoomIn}
          title={t('pdfViewer.zoomIn')}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}

interface ZoomItemProps {
  checked: boolean
  onSelect: () => void
  /** The zoom this choice comes to, shown on the right. */
  value?: number | null
  percent?: (value: number) => string
  note?: string
  children: ReactNode
}

function ZoomItem({ checked, onSelect, value, percent, note, children }: ZoomItemProps) {
  return (
    <DropdownMenuItem onSelect={onSelect} className="gap-2 py-1.5 pl-8">
      {checked && <Check className="absolute left-2.5 h-3.5 w-3.5 text-[var(--theme-interactive-color)]" />}
      <span className={checked ? 'font-semibold' : undefined}>{children}</span>
      {note && <span className="text-xs text-[var(--text-secondary)]">{note}</span>}
      {value != null && percent && (
        <span className="ml-auto text-xs tabular-nums text-[var(--text-secondary)]">{percent(value)}</span>
      )}
    </DropdownMenuItem>
  )
}

interface PageInputProps {
  pageNumber: number
  numPages: number
  onGoToPage: (page: number) => void
  label: string
}

/** The current page, editable: Enter jumps, Escape or leaving the field puts it back. */
function PageInput({ pageNumber, numPages, onGoToPage, label }: PageInputProps) {
  const [draft, setDraft] = useState(String(pageNumber))
  useEffect(() => setDraft(String(pageNumber)), [pageNumber])

  function commit() {
    const page = Number.parseInt(draft, 10)
    if (Number.isFinite(page)) onGoToPage(Math.min(Math.max(page, 1), numPages))
    setDraft(String(pageNumber))
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      aria-label={label}
      value={draft}
      onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit()
        if (e.key === 'Escape') setDraft(String(pageNumber))
      }}
      onBlur={() => setDraft(String(pageNumber))}
      className="h-6 w-9 rounded border border-[var(--window-border-color)] bg-[var(--input-bg,transparent)] text-center text-xs tabular-nums text-[var(--text-color)]"
    />
  )
}
